import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {Field, useFormikContext} from "formik";

import FunctionButton from "../../../../../../utilities/Buttons/FunctionButton";
import {swalCustom} from "../../../../../../utilities/hooks/swalCustom";
import {toaster} from "../../../../../../utilities/hooks/toaster";
import {moneyUnformat, numberFormat} from "../../../../../../utilities/moneyFormat";
import ToolTip from "../../../../../../utilities/ToolTip";
import ReactTable from "../../../../../conteiners/ReactTable";
import {MoneyInput, NumberInput, Select, TextInput} from "../../../../../Inputs";
import useColumns from "../../../../../Requests/OrderProcessing/utils/hooks/useColumns";
import {
  addNewCOfDiscount,
  deleteCOfDiscounts,
  fetchAllCOfDiscounts,
  fetchCOf,
  fetchCOfDiscountInfo,
  getPlanSkuList,
  selectAllCOfDiscounts,
  selectCOfDiscountInfo,
  selectCurrentCof,
} from "../../../KpFinSlice";
import {DISCOUNT_TABLE_COLUMNS} from "../../utils";
import {DISCOUNT_RADIOS} from "../ServicesAndDiscounts";

const DiscountsPart = ({setIsSaved, kpReadOnly = false}) => {
  const {
    t,
    i18n: {language},
  } = useTranslation();
  const dispatch = useDispatch();

  const {values, setValues, setFieldValue} = useFormikContext();
  const {
    coMoneyDiscount,
    coProcentDiscount,
    brandMoneyDiscount,
    brandProcentDiscount,
    typeMoneyDiscount,
    typeProcentDiscount,
    discountTypeId,
    discountRadio,
    factoryId,
  } = values ?? {};

  useEffect(() => {
    if (discountRadio === "cofNumber")
      setValues(prev => ({
        ...prev,
        factoryId: "",
        brandMoneyDiscount: "",
        brandProcentDiscount: 0,
      }));
    if (discountRadio === "factory")
      setValues(prev => ({
        ...prev,
        coMoneyDiscount: "",
        coProcentDiscount: 0,
      }));
  }, [discountRadio]);

  const currentCOf = useSelector(selectCurrentCof);
  const planSkuList = useSelector(getPlanSkuList);
  const cofId = currentCOf?.cofId;
  const currentCOfNumber = currentCOf?.cofNumber;
  useEffect(() => {
    setFieldValue("cofNumber", currentCOfNumber);
  }, [currentCOf?.cofId]);

  const currentSkuPlan = planSkuList?.find(({factory}) => factory.factoryId === factoryId);

  const allDiscounts = useSelector(selectAllCOfDiscounts);
  const discountInfo = useSelector(selectCOfDiscountInfo);
  const currentCOfSum = discountInfo?.cofSum;
  const currentDiscountType = discountInfo?.discountTypes?.find(({typeId}) => typeId === discountTypeId);
  const discountTypeBaseSum = currentDiscountType?.cofSum;
  const currentBrandSum = discountInfo?.factories?.find(
    ({factoryId: filterBrandId}) => filterBrandId === factoryId,
  )?.factorySum;

  const hasCurrentBrandDiscount = !!allDiscounts?.discounts?.find(
    ({factory}) => currentSkuPlan?.factory?.name === factory,
  );

  const brandsOptions = discountInfo?.factories?.map(({factoryId, name}) => ({title: name, value: factoryId})) ?? [];
  const areAllAdditionalDiscountsApplied = discountInfo?.factories?.length === 0;
  const discountTypeOptions =
    discountInfo?.discountTypes?.map(({typeId, nameEng, nameUkr}) => ({
      title: language === "en" ? nameEng : nameUkr,
      value: typeId,
    })) ?? [];

  useEffect(() => {
    dispatch(fetchAllCOfDiscounts({cofId}));
    dispatch(fetchCOfDiscountInfo({cofId}));
  }, []);

  const [currentDiscountComposedId, setCurrentDiscountComposedId] = useState(null);

  const [selectedDiscountIds, setSelectedDiscountIds] = useState([]);

  const uniqueSelectedDiscountIds = Array.from(new Set(selectedDiscountIds));

  const findIfCOfDiscountIsApplied = allDiscounts =>
    allDiscounts?.discounts
      ?.slice(1)
      ?.some(({discountId}) => discountId === (allDiscounts?.discounts ?? [])[0]?.discountId);
  const isCOfDiscountApplied = findIfCOfDiscountIsApplied(allDiscounts);

  const isTopDiscountApplied = allDiscounts?.topDisounts?.length > 0;
  const [defaultMultiSelection, setDefaultMultiSelection] = useState([]);

  const discountAllRowComposedIds =
    allDiscounts?.discounts?.map(({discountId, factory}) => discountId + "-|-" + factory) ?? [];

  const discountTableColumns = useColumns(DISCOUNT_TABLE_COLUMNS);
  const discountTableData = [
    ...(allDiscounts?.discounts?.map(({discountId, factory, discountSum, discountValue, resultSum, sum}, index) => ({
      number: index + 1,
      id: discountId + "-|-" + factory,
      factory,
      discountSum: numberFormat(discountSum),
      discountValue: numberFormat(discountValue),
      resultSum: numberFormat(resultSum),
      sum: numberFormat(sum),
    })) ?? []),
    ...(allDiscounts?.topDisounts?.map(
      ({discountId, amounts: {finalSum, topDiscountSum, resultSum}, discountValue}) => ({
        number: allDiscounts?.discounts?.length + 1,
        id: discountId + "-|-" + "top",
        factory: "-",
        discountSum: numberFormat(topDiscountSum),
        discountValue: numberFormat(discountValue),
        resultSum: numberFormat(finalSum),
        sum: numberFormat(resultSum),
      }),
    ) ?? []),
  ];

  const [isCheckedDiscountType, setIsCheckedDiscountType] = useState(false);

  const handleDiscountTypeChange = () => {
    if (isCheckedDiscountType) {
      setTimeout(() => {
        setValues(prev => ({...prev, discountTypeId: null, typeMoneyDiscount: "", typeProcentDiscount: ""}));
      });
    }
    setIsCheckedDiscountType(prev => !prev);
  };

  // const calcProcentValue = ({moneyValue, sum}) => {
  //   return (Number.parseFloat(moneyValue) / sum) * 100;
  // };

  const onAddDiscountsBtnClick = () => {
    dispatch(
      addNewCOfDiscount({
        cofId,
        factoryId: discountRadio === DISCOUNT_RADIOS.COF_NUMBER ? null : factoryId,
        discountValue: discountRadio === DISCOUNT_RADIOS.COF_NUMBER ? coProcentDiscount : brandProcentDiscount,
        // ? calcProcentValue({moneyValue: coMoneyDiscount, sum: currentCOfSum})
        // : calcProcentValue({moneyValue: brandMoneyDiscount, sum: currentBrandSum}),
        typeId: null,
      }),
    )
      .then(resp => {
        if (resp.meta.requestStatus === "fulfilled") {
          toaster.success(t("Discount added successfully"));
          dispatch(fetchAllCOfDiscounts({cofId}));
          dispatch(fetchCOfDiscountInfo({cofId}));
          setValues(prev => ({
            ...prev,
            discountRadio: "",
            factoryId: "",
            coMoneyDiscount: "",
            coProcentDiscount: 0,
            brandMoneyDiscount: "",
            brandProcentDiscount: 0,
          }));
          setIsSaved(false);
          dispatch(fetchCOf({cofId}));
        }
      })
      .catch(err => {
        console.error("err :", err);
        setValues(prev => ({
          ...prev,
          discountRadio: "",
          factoryId: "",
          coMoneyDiscount: "",
          coProcentDiscount: 0,
          brandMoneyDiscount: "",
          brandProcentDiscount: 0,
        }));
      });
  };
  const onDeleteDiscountsBtnClick = () => {
    if (!uniqueSelectedDiscountIds || uniqueSelectedDiscountIds.length === 0) return;

    swalCustom.confirm({
      confirmFunc: () => {
        dispatch(deleteCOfDiscounts({discountIds: uniqueSelectedDiscountIds})).then(resp => {
          if (resp.meta.requestStatus === "fulfilled") {
            toaster.success(t("Discount deleted successfully"));
            dispatch(fetchAllCOfDiscounts({cofId}));
            dispatch(fetchCOfDiscountInfo({cofId}));
            setCurrentDiscountComposedId(null);
            setIsSaved(false);
            setDefaultMultiSelection([]);
            setSelectedDiscountIds([]);
            dispatch(fetchCOf({cofId}));
          }
        });
      },
    });
  };

  const onAddDiscountsTypeBtnClick = () => {
    dispatch(
      addNewCOfDiscount({cofId, factoryId: null, discountValue: typeProcentDiscount, typeId: discountTypeId}),
    ).then(resp => {
      if (resp.meta.requestStatus === "fulfilled") {
        toaster.success(t("Discount type added successfully"));
        dispatch(fetchAllCOfDiscounts({cofId}));
        setValues(prev => ({...prev, discountTypeId: "", typeMoneyDiscount: "", typeProcentDiscount: 0}));
        setIsSaved(false);
        dispatch(fetchCOf({cofId}));
      }
    });
  };

  const onDiscountFieldBlur = ({
    moneyFieldName,
    moneyFieldValue,
    procentFieldName,
    procentFieldValue,
    hasMoneyFieldChanged,
    sum,
    message,
  }) => {
    if (!sum) {
      toaster.warning(message ?? "Current sku sum is null");
      return;
    }

    if (hasMoneyFieldChanged) {
      // поменяли сумму в евро
      const newProcentValue = (moneyUnformat(moneyFieldValue) / sum) * 100;

      if (newProcentValue > 100) {
        // процент не может получиться больше 100
        toaster.error("Procent can`t be bigger than 100!", 2000);
        setFieldValue(moneyFieldName, "");
        setFieldValue(procentFieldName, 0);
        return;
      }
      setFieldValue(procentFieldName, newProcentValue);
    } else {
      //поменяли процент
      setFieldValue(moneyFieldName, sum * (Number.parseFloat(procentFieldValue) / 100));
    }
  };

  const onDiscountTableMultiSelect = selectedRows => {
    if (selectedRows.length === 0) {
      setSelectedDiscountIds([]);
      setDefaultMultiSelection([]);
      return;
    }
    if (
      isCOfDiscountApplied &&
      selectedDiscountIds?.length > 0 &&
      selectedRows.join("") !== discountAllRowComposedIds.join("")
    ) {
      setSelectedDiscountIds([]);
      setDefaultMultiSelection([]);
      return;
    }
    if (
      isCOfDiscountApplied &&
      selectedDiscountIds?.length === 0 &&
      discountAllRowComposedIds.join("").includes(selectedRows.join(""))
    ) {
      setDefaultMultiSelection(discountAllRowComposedIds);
    }
    setSelectedDiscountIds(selectedRows.map(row => row.split("-|-")[0]));
    setDefaultMultiSelection(selectedRows);
  };

  return (
    <div className="fdr gap aistr">
      <div className="fcCol gap5 aistr" style={{width: "53%"}}>
        <div className="grid2ColSecondBigger gap aibl">
          <ToolTip
            title={isCOfDiscountApplied ? "A discount for this COf already exists" : ""}
            className="row aibl gap jcfs"
          >
            <>
              <Field
                type="radio"
                name="discountRadio"
                value={DISCOUNT_RADIOS.COF_NUMBER}
                disabled={areAllAdditionalDiscountsApplied || kpReadOnly}
              />
              <TextInput label="CO(f) additional" name="cofNumber" width="13em" disabled />
            </>
          </ToolTip>

          <div className="fdr aife gap padding5 fGrow" style={{border: "1px solid #bdd2f1"}}>
            <MoneyInput
              label="Discount"
              name="coMoneyDiscount"
              width="6em"
              onBlur={e => {
                onDiscountFieldBlur({
                  moneyFieldName: "coMoneyDiscount",
                  moneyFieldValue: e.target.value,
                  procentFieldName: "coProcentDiscount",
                  procentFieldValue: coProcentDiscount,
                  hasMoneyFieldChanged: true,
                  sum: currentCOfSum,
                });
              }}
              disabled={areAllAdditionalDiscountsApplied || discountRadio !== DISCOUNT_RADIOS.COF_NUMBER || kpReadOnly}
            />
            <div style={{width: "6em"}}>{t("or discount")}</div>
            <NumberInput
              name="coProcentDiscount"
              width="5em"
              className="procent"
              min={0}
              max={80}
              maxNumber={100}
              onBlur={e => {
                if (values?.coMoneyDiscount) return;
                onDiscountFieldBlur({
                  moneyFieldName: "coMoneyDiscount",
                  moneyFieldValue: coMoneyDiscount,
                  procentFieldName: "coProcentDiscount",
                  procentFieldValue: e.target.value,
                  hasMoneyFieldChanged: false,
                  sum: currentCOfSum,
                });
              }}
              disabled={areAllAdditionalDiscountsApplied || discountRadio !== DISCOUNT_RADIOS.COF_NUMBER || kpReadOnly}
              readOnly={!!values?.coMoneyDiscount}
              formatOnlyView
            />
          </div>
        </div>
        <div className="grid2ColSecondBigger gap aibl">
          <ToolTip
            title={
              hasCurrentBrandDiscount
                ? "Current brand has already a discount"
                : isCOfDiscountApplied
                ? "Can't apply brand discount if CO(f) discount exists"
                : ""
            }
            className="row aibl gap jcfs"
          >
            <>
              <Field
                type="radio"
                name="discountRadio"
                value={DISCOUNT_RADIOS.BRAND}
                disabled={areAllAdditionalDiscountsApplied || kpReadOnly}
                // disabled={isCOfDiscountApplied}
              />
              <Select
                options={brandsOptions}
                label="Factory"
                name="factoryId"
                width="14rem"
                disabled={areAllAdditionalDiscountsApplied || discountRadio !== DISCOUNT_RADIOS.BRAND || kpReadOnly}
                // disabled={isCOfDiscountApplied}
              />
            </>
          </ToolTip>

          <div className="fdr aife gap padding5 fGrow" style={{border: "1px solid #bdd2f1"}}>
            <MoneyInput
              label="Discount"
              name="brandMoneyDiscount"
              width="6em"
              onBlur={e => {
                onDiscountFieldBlur({
                  moneyFieldName: "brandMoneyDiscount",
                  moneyFieldValue: e.target.value,
                  procentFieldName: "brandProcentDiscount",
                  procentFieldValue: brandProcentDiscount,
                  hasMoneyFieldChanged: true,
                  sum: currentBrandSum,
                });
              }}
              disabled={
                !factoryId || areAllAdditionalDiscountsApplied || discountRadio !== DISCOUNT_RADIOS.BRAND || kpReadOnly
              }
            />

            <div style={{width: "6em"}}>{t("or discount")}</div>
            <NumberInput
              name="brandProcentDiscount"
              width="5em"
              className="procent"
              min={0}
              max={80}
              maxNumber={100}
              onBlur={e => {
                if (values?.brandProcentDiscount) return;
                onDiscountFieldBlur({
                  moneyFieldName: "brandMoneyDiscount",
                  moneyFieldValue: brandMoneyDiscount,
                  procentFieldName: "brandProcentDiscount",
                  procentFieldValue: e.target.value,
                  hasMoneyFieldChanged: false,
                  sum: currentBrandSum,
                });
              }}
              readOnly={!!values?.brandProcentDiscount || kpReadOnly}
              disabled={!factoryId || areAllAdditionalDiscountsApplied || discountRadio !== DISCOUNT_RADIOS.BRAND}
              formatOnlyView
            />
          </div>
        </div>
        <div className="row jcfe gap3x w100 mb0_5">
          <FunctionButton
            name="Delete"
            disabled={
              !uniqueSelectedDiscountIds ||
              uniqueSelectedDiscountIds.length === 0 ||
              (uniqueSelectedDiscountIds.length === 1 &&
                +uniqueSelectedDiscountIds[0] === (allDiscounts?.topDisounts ?? [])[0]?.discountId) ||
              kpReadOnly
            }
            onClick={onDeleteDiscountsBtnClick}
          />
          <FunctionButton
            name="Add"
            disabled={
              !discountRadio ||
              (discountRadio === DISCOUNT_RADIOS.COF_NUMBER && (!coMoneyDiscount || !coProcentDiscount)) ||
              (discountRadio === DISCOUNT_RADIOS.BRAND &&
                (!brandMoneyDiscount || !brandProcentDiscount || !factoryId)) ||
              // (discountRadio === DISCOUNT_RADIOS.COF_NUMBER && isCOfDiscountApplied) ||
              (discountRadio === DISCOUNT_RADIOS.BRAND && hasCurrentBrandDiscount) ||
              kpReadOnly
              // ||
              // (discountRadio === DISCOUNT_RADIOS.COF_NUMBER && allDiscounts?.discounts?.length > 0)
            }
            onClick={onAddDiscountsBtnClick}
            bottomTooltip
            tooltipMessage={
              kpReadOnly
                ? ""
                : discountRadio === DISCOUNT_RADIOS.COF_NUMBER && isCOfDiscountApplied
                ? "A discount for this COf already exists"
                : discountRadio === DISCOUNT_RADIOS.BRAND && hasCurrentBrandDiscount
                ? "Current brand has already a discount"
                : !discountRadio
                ? "сhoose discount type radio btn"
                : ""
            }
          />
        </div>

        <div className="grid2ColSecondBigger gap aibl">
          <div>
            <ToolTip
              title={isTopDiscountApplied ? "Top discount already applied" : ""}
              bottom
              className="row aic gap jcfs"
            >
              <>
                <Field
                  name=""
                  type="checkbox"
                  checked={isCheckedDiscountType}
                  onChange={handleDiscountTypeChange}
                  disabled={isTopDiscountApplied || kpReadOnly}
                />
                <Select
                  options={discountTypeOptions}
                  label="Disc top type"
                  name="discountTypeId"
                  disabled={isTopDiscountApplied || !isCheckedDiscountType || kpReadOnly}
                />
              </>
            </ToolTip>
          </div>

          <div className="fdr aife gap padding5" style={{border: "1px solid #bdd2f1"}}>
            <MoneyInput
              label="Discount"
              name="typeMoneyDiscount"
              disabled={isTopDiscountApplied || !discountTypeId || !isCheckedDiscountType || kpReadOnly}
              width="6em"
              onBlur={e => {
                onDiscountFieldBlur({
                  moneyFieldName: "typeMoneyDiscount",
                  moneyFieldValue: e.target.value,
                  procentFieldName: "typeProcentDiscount",
                  procentFieldValue: typeProcentDiscount,
                  hasMoneyFieldChanged: true,
                  sum: discountTypeBaseSum,
                  message: "Select discount type",
                });
              }}
            />
            <div style={{width: "6em"}}>{t("or discount")}</div>
            <NumberInput
              name="typeProcentDiscount"
              width="5em"
              className="procent"
              disabled={isTopDiscountApplied || !discountTypeId || kpReadOnly}
              readOnly={!!values?.typeMoneyDiscount}
              min={0}
              max={80}
              maxNumber={100}
              onBlur={e => {
                if (values?.typeMoneyDiscount) return;
                onDiscountFieldBlur({
                  moneyFieldName: "typeMoneyDiscount",
                  moneyFieldValue: typeMoneyDiscount,
                  procentFieldName: "typeProcentDiscount",
                  procentFieldValue: e.target.value,
                  hasMoneyFieldChanged: false,
                  sum: discountTypeBaseSum,
                  message: "Select discount type",
                });
              }}
              formatOnlyView
            />
          </div>
        </div>
        <div className="row jcfe gap3x w100">
          <FunctionButton
            name="Delete"
            disabled={
              !isTopDiscountApplied ||
              !uniqueSelectedDiscountIds ||
              uniqueSelectedDiscountIds.length === 0 ||
              selectedDiscountIds.length > 1 ||
              (uniqueSelectedDiscountIds.length === 1 &&
                +uniqueSelectedDiscountIds[0] !== (allDiscounts?.topDisounts ?? [])[0]?.discountId) ||
              kpReadOnly
            }
            onClick={onDeleteDiscountsBtnClick}
          />
          <FunctionButton
            name="Add"
            disabled={
              isTopDiscountApplied || !typeProcentDiscount || !discountTypeId || !isCheckedDiscountType || kpReadOnly
            }
            tooltipMessage={
              isTopDiscountApplied
                ? "Top discount already applied"
                : !typeProcentDiscount || !discountTypeId || !isCheckedDiscountType
                ? "addDiscountTypeBtnDisabledTooltip"
                : ""
            }
            bottomTooltip
            onClick={onAddDiscountsTypeBtnClick}
          />
        </div>
      </div>
      <div className="fGrow">
        <ReactTable
          columns={discountTableColumns}
          defaultData={discountTableData}
          enableMultiSelection={kpReadOnly ? false : true}
          className="scrollX scrollY"
          tableClassName="fGrow"
          current={currentDiscountComposedId}
          onSelect={setCurrentDiscountComposedId}
          selectable={false}
          setMultiSelect={onDiscountTableMultiSelect}
          defaultMultiSelection={defaultMultiSelection}
        />
      </div>
    </div>
  );
};

export default DiscountsPart;
